"use server";

import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { getBillingEnabledAction } from "@/lib/api/billing-actions";
import { IMPERSONATION_COOKIE, SESSION_COOKIE } from "@/lib/session";

const BASE = process.env.BACKEND_INTERNAL_URL ?? "http://erp-backend:4000";
const REMEMBER_MAX_AGE = 60 * 60 * 24 * 30; // 30 days (auth-actions parity)

/** Same XFF idiom as signup-actions: the backend's req.ip must be the real
 * client for throttling and audit, not the SSR container. */
async function forwardHeaders(): Promise<Record<string, string>> {
  const h = await headers();
  const xff = h.get("x-forwarded-for");
  return {
    "Content-Type": "application/json",
    ...(xff ? { "x-forwarded-for": xff } : {}),
  };
}

async function establishSession(
  token: string,
  target: string = "/panel",
): Promise<never> {
  const jar = await cookies();
  jar.set(SESSION_COOKIE, token, {
    httpOnly: true,
    secure: true,
    sameSite: "lax",
    path: "/",
    maxAge: REMEMBER_MAX_AGE,
  });
  // A fresh login must never inherit a previous impersonation context.
  jar.delete(IMPERSONATION_COOKIE);
  redirect(target);
}

export async function getGoogleStatusAction(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/auth/google/status`, { cache: "no-store" });
    if (!res.ok) return false;
    const body = (await res.json()) as { enabled?: boolean };
    return body.enabled === true;
  } catch {
    return false;
  }
}

export type GoogleExchangeError = { ok: false; error: string };

export async function exchangeGoogleOttAction(
  ott: string,
  slug?: string,
): Promise<GoogleExchangeError> {
  let token: string;
  try {
    const res = await fetch(`${BASE}/auth/google/exchange`, {
      method: "POST",
      headers: await forwardHeaders(),
      body: JSON.stringify({ ott, ...(slug ? { slug } : {}), remember: true }),
      cache: "no-store",
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        message?: string;
      } | null;
      return {
        ok: false,
        error:
          typeof body?.message === "string"
            ? body.message
            : "Logowanie przez Google nie powiodło się. Spróbuj ponownie.",
      };
    }
    ({ token } = (await res.json()) as { token: string });
  } catch {
    return {
      ok: false,
      error: "Logowanie przez Google nie powiodło się. Spróbuj ponownie.",
    };
  }
  return establishSession(token);
}

export type GoogleSignupResult =
  | { status: "slug_taken" }
  | { status: "expired" }
  | { status: "disabled" }
  | { status: "invalid"; message: string }
  | { status: "error" };

export async function signupGoogleAction(input: {
  ott: string;
  companyName: string;
  nip: string;
  slug: string;
  adminName: string;
  consentsAccepted: boolean;
}): Promise<GoogleSignupResult> {
  let token: string;
  try {
    const res = await fetch(`${BASE}/signup/google`, {
      method: "POST",
      headers: await forwardHeaders(),
      body: JSON.stringify(input),
      cache: "no-store",
    });
    if (!res.ok) {
      if (res.status === 409) return { status: "slug_taken" };
      if (res.status === 401) return { status: "expired" };
      if (res.status === 403) return { status: "disabled" };
      if (res.status === 400 || res.status === 422) {
        const body = (await res.json().catch(() => null)) as {
          message?: string | string[];
        } | null;
        const message = Array.isArray(body?.message)
          ? (body.message[0] ?? "Nieprawidłowe dane.")
          : (body?.message ?? "Nieprawidłowe dane.");
        return { status: "invalid", message };
      }
      return { status: "error" };
    }
    ({ token } = (await res.json()) as { token: string });
  } catch {
    return { status: "error" };
  }
  // Post-signup only (login keeps the default /panel target): route a fresh
  // tenant to the card step when this environment has billing switched on.
  const billingEnabled = await getBillingEnabledAction();
  return establishSession(
    token,
    billingEnabled ? "/panel/rozliczenia/karta?powitanie=1" : "/panel",
  );
}
